// Calendar sync on the office Settings page (Session ATY). Guvnor keeps four Google calendars on the info@ account up
// to date with the schedule (server/src/modules/calendarSync/). This section shows what each carries, whether it is on,
// how many events Guvnor keeps there, when the sync last ran and what its last full check did.
//
// WHO CAN DO WHAT, decided by the server and mirrored here so nobody is offered a control that is refused:
//   - everyone who can open Settings (office or admin) sees the state and may ask for a resync now;
//   - only an admin switches a calendar on or off (PATCH /api/calendar-sync/settings is admin only), and only an admin
//     is offered the Google reconnect, which is the same OAuth handshake the Integrations section starts.
// Turning a calendar OFF removes the events Guvnor put there, so it is a two-step branded confirm naming the effect;
// turning one ON puts them back and is a single press. Both server writes preview unless `preview: false` is sent;
// api.calendarSync sends it, because the confirm is the button that precedes the call.
//
// The status is read live (noCache) on every visit and after every change: it is the platform's own view of Google,
// and a cached copy would show a calendar on after it was switched off.
import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import type { CalendarSyncKey, CalendarSyncStatus } from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { ConfirmButton } from "../../components/ConfirmButton";
import { friendlyApiError } from "../../lib/friendlyApiError";
import { Loader } from "../../components/Loader";
import { StatusPill } from "../../components/StatusPill";
import { formatDateTime } from "../../lib/format";
import { Button } from "../../components/Button";

// "Not yet" for a missing time (formatDateTime alone would print "-").
function lastTimeText(iso: string | null): string {
  const text = formatDateTime(iso);
  return text === "-" ? "Not yet" : text;
}

// The last full check's counts in plain words, in the order the office reads them.
export function reconcileCountsInWords(c: NonNullable<CalendarSyncStatus["lastReconcileCounts"]>): string {
  return [
    `${c.created} written`,
    `${c.updated} updated`,
    `${c.repaired} put back after an edit in Google`,
    `${c.removed} removed`,
    `${c.unchanged} unchanged`,
    `${c.failed} failed`,
  ].join(", ");
}

function eventsLabel(n: number): string {
  return `${n} event${n === 1 ? "" : "s"}`;
}

function feedsLine(n: number): string {
  if (n === 0) return "No engineers have their jobs in their own calendar yet. Each engineer adds theirs from the engineer app.";
  if (n === 1) return "1 engineer has their jobs in their own calendar.";
  return `${n} engineers have their jobs in their own calendar.`;
}

const BTN =
  "rounded-ele border border-ele-purple-accent px-3 py-1 font-menu text-xs tracking-wide text-ele-body transition hover:border-ele-green hover:text-ele-strong disabled:opacity-50";

export function CalendarSyncSection({ refreshKey = 0 }: { refreshKey?: number }) {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";

  const [status, setStatus] = useState<CalendarSyncStatus | null>(null);
  const [loadState, setLoadState] = useState<"loading" | "ready" | "error">("loading");
  const [reloadKey, setReloadKey] = useState(0);

  const [switching, setSwitching] = useState<CalendarSyncKey | null>(null);
  const [switchMsg, setSwitchMsg] = useState<string | null>(null);
  const [switchErr, setSwitchErr] = useState<string | null>(null);

  const [resyncBusy, setResyncBusy] = useState(false);
  const [resyncMsg, setResyncMsg] = useState<string | null>(null);
  const [resyncErr, setResyncErr] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    // An async body so a missing or throwing client method is a caught failure, never an error thrown out of the
    // effect (which would take the whole Settings page down with it).
    (async () => {
      try {
        const s = await api.calendarSync.status();
        if (!live) return;
        setStatus(s);
        setLoadState("ready");
      } catch {
        if (live) setLoadState("error");
      }
    })();
    return () => {
      live = false;
    };
  }, [refreshKey, reloadKey]);

  async function setCalendar(key: CalendarSyncKey, on: boolean) {
    setSwitching(key);
    setSwitchMsg(null);
    setSwitchErr(null);
    try {
      const res = await api.calendarSync.setCalendars({ [key]: on });
      const change = res?.changes?.find((c) => c.key === key);
      setSwitchMsg(change?.effect ?? null);
      if (res?.status) {
        setStatus(res.status);
        setLoadState("ready");
      } else {
        setReloadKey((k) => k + 1);
      }
    } catch (err) {
      setSwitchErr(friendlyApiError(err, "Could not change that calendar. Please try again."));
    } finally {
      setSwitching(null);
    }
  }

  async function resync() {
    setResyncBusy(true);
    setResyncMsg(null);
    setResyncErr(null);
    try {
      await api.calendarSync.resync();
      setResyncMsg("Queued. The counts update here when it finishes.");
      setReloadKey((k) => k + 1);
    } catch (err) {
      // 409 (Google lacks the calendar permission) and 503 (the queue is unavailable) carry the server's own sentence.
      setResyncErr(friendlyApiError(err, "Could not start the check. Please try again."));
    } finally {
      setResyncBusy(false);
    }
  }

  return (
    <section className="mt-5 rounded-ele border border-ele-purple-accent bg-ele-surface p-5" aria-labelledby="calendar-sync-heading">
      <h2 id="calendar-sync-heading" className="mb-3 font-heading text-lg text-ele-blue">
        Calendar sync
      </h2>
      <p className="mb-4 text-sm text-ele-body">
        Guvnor keeps four Google calendars on the info@ account up to date with the schedule. It only ever writes to them: a
        change made in Google Calendar is put back at the next sync.
      </p>

      {loadState === "loading" && !status ? (
        <Loader shape="inline" />
      ) : !status ? (
        // Not role="alert": a load failure here is not urgent, and this section shares the Settings page with forms
        // whose own errors are the alerts a person needs to hear.
        <p role="status" className="text-sm text-ele-strong">
          Could not load the calendar sync status. Use Refresh to try again.
        </p>
      ) : (
        <>
          {status.access !== "connected" && (
            <div className="mb-4 rounded-ele border border-amber-500 bg-amber-500/10 px-3 py-2 text-sm text-[var(--ele-amber-text-soft)]">
              <p>{status.accessMessage ?? "Calendar sync cannot run until Google is connected."}</p>
              {isAdmin ? (
                <Button size="sm" className="mt-2 w-fit" onClick={() => window.location.assign(api.integrations.connectHref("google"))}>
                  {status.access === "needs_reconnect" ? "Reconnect Google" : "Connect Google"}
                </Button>
              ) : (
                <p className="mt-1">An administrator can do this from Settings, under Integrations.</p>
              )}
            </div>
          )}

          {loadState === "error" && (
            <p role="status" className="mb-3 text-xs text-ele-body">
              Could not refresh just now. Showing what was last read.
            </p>
          )}

          <ul className="space-y-3" aria-label="Calendars">
            {status.calendars.map((c) => (
              <li key={c.key} className="rounded-ele border border-ele-purple-accent bg-ele-surface p-3" data-testid={`calendar-row-${c.key}`}>
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="min-w-0 flex-1 basis-56">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-sm text-ele-strong">{c.name}</span>
                      <StatusPill word={c.on ? "On" : "Off"} tone={c.on ? "green" : "grey"} />
                      <span className="text-xs text-ele-body">{eventsLabel(c.events)}</span>
                    </div>
                    <p className="mt-1 text-sm text-ele-body">{c.carries}</p>
                    {c.on && !c.ready && (
                      <p className="mt-1 text-xs text-ele-body">Not set up in Google yet: Guvnor creates it at the next sync.</p>
                    )}
                  </div>
                  {isAdmin && (
                    <div className="max-w-full flex-none">
                      {c.on ? (
                        <ConfirmButton
                          label={`Turn off ${c.name}`}
                          confirmLabel={`Turn off ${c.name}? Guvnor removes the events it put there.`}
                          onConfirm={() => void setCalendar(c.key, false)}
                          busy={switching === c.key}
                          busyLabel="Turning off..."
                          disabled={switching !== null && switching !== c.key}
                          resetMs={6000}
                          className={BTN}
                        />
                      ) : (
                        <button
                          type="button"
                          onClick={() => void setCalendar(c.key, true)}
                          disabled={switching !== null}
                          className={`${BTN} max-sm:min-h-11`}
                        >
                          {switching === c.key ? "Turning on..." : `Turn on ${c.name}`}
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </li>
            ))}
          </ul>

          {switchMsg && (
            <p role="status" className="mt-3 text-sm text-ele-green">
              {switchMsg}
            </p>
          )}
          {switchErr && (
            <p role="alert" className="mt-3 rounded-ele border border-ele-danger bg-ele-surface px-3 py-2 text-sm text-ele-strong">
              {switchErr}
            </p>
          )}

          <dl className="mt-4 space-y-1 text-sm">
            <div className="flex flex-wrap justify-between gap-3">
              <dt className="text-ele-body">Last sync</dt>
              <dd className="text-ele-strong">{lastTimeText(status.lastSyncAt)}</dd>
            </div>
            <div className="flex flex-wrap justify-between gap-3">
              <dt className="text-ele-body">Last full check</dt>
              <dd className="text-right text-ele-strong">
                {lastTimeText(status.lastReconcileAt)}
                {status.lastReconcileCounts && (
                  <span className="block text-xs text-ele-body">{reconcileCountsInWords(status.lastReconcileCounts)}</span>
                )}
              </dd>
            </div>
          </dl>

          {status.reconcileOverdue && status.reconcileOverdueMessage && (
            <p role="status" className="mt-3 rounded-lg border border-amber-500 bg-amber-500/10 px-3 py-2 text-sm text-[var(--ele-amber-text-soft)]">
              {status.reconcileOverdueMessage}
            </p>
          )}

          {status.lastError && (
            <p role="alert" className="mt-3 rounded-ele border border-ele-danger bg-ele-surface px-3 py-2 text-sm text-ele-strong">
              {status.lastError}
              {status.lastErrorAt && <span className="block text-xs text-ele-body">{lastTimeText(status.lastErrorAt)}</span>}
            </p>
          )}

          <div className="mt-4 flex flex-wrap items-center gap-3">
            <ConfirmButton
              label="Resync now"
              confirmLabel="Check every event against Guvnor now?"
              onConfirm={() => void resync()}
              busy={resyncBusy}
              busyLabel="Queuing..."
              resetMs={6000}
              className={BTN}
            />
            <span className="text-xs text-ele-body">
              Compares every event with the schedule, puts back anything edited in Google and removes what is no longer needed.
            </span>
          </div>
          {resyncMsg && (
            <p role="status" className="mt-2 text-sm text-ele-green">
              {resyncMsg}
            </p>
          )}
          {resyncErr && (
            <p role="alert" className="mt-2 rounded-ele border border-ele-danger bg-ele-surface px-3 py-2 text-sm text-ele-strong">
              {resyncErr}
            </p>
          )}

          <h3 className="mt-5 mb-1 font-heading text-sm text-ele-strong">How to see these in your own Google Calendar</h3>
          <p className="text-sm text-ele-body">{status.howToAdd}</p>

          <p className="mt-4 text-sm text-ele-body">{feedsLine(status.engineerFeedsOn)}</p>
        </>
      )}
    </section>
  );
}
